import { Component, OnInit, Input, ChangeDetectorRef } from '@angular/core'
import { ConfirmedSessionsService } from '@app/core/_services/confirmed-sessions.service'
import { UtilsService } from '@app/core/_services/utils.service'
import { takeUntil } from 'rxjs/operators'
import { Container, ConfirmedSessionsResponse } from '@app/core/_models'
import { Subject } from 'rxjs'
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min'
import { User } from '@app/core/_models'
import { UserService } from '@app/core/_services/user.service'
import { GroupInfo } from '@app/core/_models/rest_api'
import { SessionTileSource } from '@app/core/_services/analytics.service'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { MAX_SET_TIMEOUT_VALUE } from '@app/core/_services/time-utilities.service'
import { BookedSessionTileComponent } from '@app/routes/dashboard/components/booked-session-tile/booked-session-tile.component'
import { EpochToTimePipe } from '@app/core/pipes/epoch-to-time.pipe'
import { SharedModule } from '@app/shared/shared.module'

@Component({
    selector: 'fm-upcoming-session-panel',
    templateUrl: './upcoming-session-panel.component.html',
    styleUrls: ['./upcoming-session-panel.component.scss'],
    standalone: true,
    imports: [BookedSessionTileComponent, EpochToTimePipe, SharedModule],
})
export class UpcomingSessionPanelComponent implements OnInit {
    public timeZone: string
    private ngUnsubscribe: Subject<any> = new Subject<any>()
    public currentTime: number
    public profileInfo: User
    public group: GroupInfo[] = []
    faPlus = faPlus

    private confirmedSessions: Container<ConfirmedSessionsResponse> =
        new Container<ConfirmedSessionsResponse>()

    private refreshTimerStarted: boolean = false
    private refreshTimer: any
    public SessionTileSource = SessionTileSource

    public sessionToShow: ConfirmedSessionsResponse = null
    public showTimer: boolean = false
    public sessionDateStr: string = null
    public isSessionOngoing: boolean = false

    constructor(
        private userService: UserService,
        private utilsService: UtilsService,
        private confirmedSessionsService: ConfirmedSessionsService,
    ) {}

    ngOnInit() {
        this.userService.currentUser.pipe(takeUntil(this.ngUnsubscribe)).subscribe((user: User) => {
            this.profileInfo = user
            this.timeZone = user.timeZone
        })

        this.confirmedSessionsService.confirmedSessionsSubject
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((conf_sessions: Container<ConfirmedSessionsResponse>) => {
                this.confirmedSessions = conf_sessions
                this.processConfirmedSessions()
            })
    }

    processConfirmedSessions() {
        if (!this.timeZone) {
            return
        }

        this.startRefreshTimer()
        this.sessionToShow = null
        this.showTimer = false
        this.sessionDateStr = null
        this.isSessionOngoing = false
        this.currentTime = this.utilsService.getCurrentTimestamp()

        // get the index of the earliest session that hasn't ended yet
        let index = this.confirmedSessions.getArray().findIndex((session) => {
            return !this.utilsService.hasSessionEnded(
                session.sessionTime,
                session.duration,
                this.currentTime,
            )
        })

        if (index === -1) {
            // all sessions have ended (or there are no sessions)
            return
        }

        // if that session is not in a state that should be displayed, check the next one.
        let session = this.confirmedSessions.getByIndex(index)
        if (
            !this.utilsService.shouldDisplaySession(
                session.sessionType,
                session.sessionTime,
                session.state,
                !!session.partnerId,
            )
        ) {
            index++
            session = this.confirmedSessions.getByIndex(index)
            if (
                !session ||
                !this.utilsService.shouldDisplaySession(
                    session.sessionType,
                    session.sessionTime,
                    session.state,
                    !!session.partnerId,
                )
            ) {
                return
            }
        }

        // we've found a session to display! ayyyy
        this.sessionToShow = session

        if (
            !!this.sessionToShow.partnerId &&
            this.utilsService.isTenMinsToSessionStart(session.sessionTime, this.currentTime)
        ) {
            // show the timer if we have a partner and the session is about to start
            this.showTimer = true
        } else {
            // otherwise show the date (or today)
            this.showTimer = false
            if (this.utilsService.isSessionToday(session.sessionTime, this.timeZone)) {
                this.sessionDateStr = 'Today'
            } else {
                this.sessionDateStr = moment(session.sessionTime)
                    .tz(this.timeZone)
                    .format('dddd, MMM D')
            }
        }

        if (
            this.utilsService.ongoingSession(
                session.sessionTime,
                session.duration,
                this.currentTime,
            )
        ) {
            this.isSessionOngoing = true
        } else {
            this.isSessionOngoing = false
        }
    }

    startRefreshTimer() {
        let now = this.utilsService.getCurrentTimestamp()
        if (this.refreshTimerStarted) {
            clearTimeout(this.refreshTimer)
            this.refreshTimerStarted = false
        }

        let length: number = this.confirmedSessions.length()

        for (let i = 0; i < length; ++i) {
            let timeoutValue: number =
                this.confirmedSessions.getByIndex(i).sessionTime - now - this.utilsService.TEN_MINS

            if (
                now <
                    this.confirmedSessions.getByIndex(i).sessionTime - this.utilsService.TEN_MINS &&
                timeoutValue < MAX_SET_TIMEOUT_VALUE
            ) {
                this.refreshTimerStarted = true
                this.refreshTimer = setTimeout(() => {
                    this.processConfirmedSessions()
                }, timeoutValue)
                break
            }
        }
    }

    ngOnDestroy() {
        if (this.refreshTimerStarted) {
            clearTimeout(this.refreshTimer)
        }
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
    }
}
